import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { GridColDef, GridMoreVertIcon } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "../../src/assets/AddIcon.svg";
import {
  getAssemblyConstituencyByZoneId,
  removeAssemblyConstituencyFromUser,
} from "../api/requests/assemblyConstituency";
import CommDataGrid from "../components/CommDataGrid";
import DeleteDialog from "../components/common/DeleteDialog";
import CreateOrEditAssemblyConstituency from "../components/CreateOrEditAssemblyConstituency";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { commonStrings } from "../static/commonStrings";
import {
  AssemblyConstituencyProps,
  AssemblyConstituencyType,
} from "../utils/types";
import { menuItemStyles } from "./Groups";
import { containedButtonWithIconSx } from "./Users";

const AssemblyConstituency = ({ selectedZone }: AssemblyConstituencyProps) => {
  const [assemblyConstituencyList, setAssemblyConstituencyList] = useState<
    AssemblyConstituencyType[]
  >([]);
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] =
    useState<AssemblyConstituencyType | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [isEditDialog, setIsEditDialog] = useState<boolean>(false);

  const getAssemblyConstituencyList = async () => {
    const assemblyConstituencyResponse = await getAssemblyConstituencyByZoneId(
      selectedZone.zoneId
    );
    if (assemblyConstituencyResponse.status == 200) {
      const formatted = assemblyConstituencyResponse.data.map(
        (item: AssemblyConstituencyType) => ({
          id: item.assemblyConstituencyId,
          ...item,
        })
      );

      setAssemblyConstituencyList(formatted);
      setTotalRowsCount(formatted.length || 0);
    }

    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    getAssemblyConstituencyList();
  }, [selectedZone.zoneId]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
  }, []);

  const handleEdit = () => {
    if (selectedRow) {
      handleMenuClose();
    }
    setOpenCreateDialog(true);
    setIsEditDialog(true);
  };
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: AssemblyConstituencyType
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    try {
      const removeAssemblyConstituencyRes =
        await removeAssemblyConstituencyFromUser(
          selectedRow.assemblyConstituencyId
        );
      if (removeAssemblyConstituencyRes.status == 200) {
        showAlert("Deleted successfully", "success");
        setSelectedRow(null);
        handleMenuClose();
        closeCreateDialog();
        getAssemblyConstituencyList();
      }
    } catch (error: any) {
      showAlert("errorMessage", "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const createAssemblyConstituency = () => {
    setOpenCreateDialog(true);
  };

  const buttonsComponent = useCallback(
    () => (
      <>
        {user?.permissions && (
          <>
            {user.permissions.some(
              (p) => p.module === "Users" && p.actions.includes("Create")
            ) ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={createAssemblyConstituency}
                  sx={containedButtonWithIconSx}
                >
                  <img src={AddIcon} /> Add Assembly Constituency
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </>
    ),
    [user]
  );

  const handleSaveAssemblyConstituency = async () => {
    closeCreateDialog();
    getAssemblyConstituencyList();
  };

  const closeCreateDialog = () => {
    setOpenCreateDialog(false);
    setOpenDeleteDialog(false);
    setIsEditDialog(false);
  };

  const filteredList = assemblyConstituencyList.filter((ac) =>
    ac.assemblyConstituencyName.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: "assemblyConstituency",
      headerName: "Assembly Constituency",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <Typography key={row.id} variant="body2" sx={{ display: "contents" }}>
            {row.assemblyConstituencyName || "--"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          {params.row.status !== "Deleted" && (
            <>
              <IconButton
                onClick={(event) => {
                  handleMenuOpen(event, params.row);
                }}
              >
                <GridMoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={menuItemStyles}
              >
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) => p.module === "Users" && p.actions.includes("Edit")
                    ) ? (
                      <MenuItem onClick={handleEdit}>Edit</MenuItem>
                    ) : null}
                  </>
                )}{" "}
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) =>
                        p.module === "Users" && p.actions.includes("Delete")
                    ) ? (
                      <MenuItem
                        onClick={() => selectedRow && setOpenDeleteDialog(true)}
                      >
                        Delete
                      </MenuItem>
                    ) : null}
                  </>
                )}{" "}
              </Menu>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Box>
      <CommDataGrid
        title={`Assembly Constituency of ${selectedZone.zoneName}`}
        description="View and manage all AssemblyConstituency in the system"
        rows={filteredList}
        columns={columns}
        section="Assembly Constituency"
        renderButtons={buttonsComponent}
        totalRowsCount={totalRowsCount}
        searchText={searchText}
        handleSearch={handleSearch}
        loading={loading}
      />
      {openCreateDialog ? (
        <CreateOrEditAssemblyConstituency
          onSaveAssemblyConstituencyCB={handleSaveAssemblyConstituency}
          setOpenCreateDialog={closeCreateDialog}
          isEdit={isEditDialog}
          assemblyConstituencyData={selectedRow!}
          selectedZone={selectedZone}
        />
      ) : null}
      {openDeleteDialog ? (
        <DeleteDialog
          actionButton={handleDelete}
          cancelButton={() => setOpenDeleteDialog(!openDeleteDialog)}
          dialogSubTitle={`Assembly Constituency ${selectedRow?.assemblyConstituencyName} will be deleted, if you click on 'Yes'`}
          dialogTextTitle={`Are you sure want to delete ${selectedRow?.assemblyConstituencyName}?`}
          title={`Delete ${commonStrings.sideBarMenuTitles.assemblyConstituency}`}
        />
      ) : null}
    </Box>
  );
};

export default AssemblyConstituency;
